"use client"

import * as React from "react"
import { BrandCardList } from "./brand-card-list"
import type { IndexBrand } from "./types"

interface BrandSectionProps {
  /** Letter bucket (`A`–`Z` or `#`). */
  letter: string
  brands: IndexBrand[]
  /** Forwarded ref hook so the parent IntersectionObserver can track this
   *  section's position in the viewport for A–Z rail highlighting. */
  sectionRef: (el: HTMLElement | null) => void
}

/**
 * One letter-section block on the `/brands` index: a 36×36 letter badge +
 * count label + hairline, followed by a 3-col card grid of `BrandCardList`
 * entries.
 *
 * FRIDGE-1496 (owner, 10/05/2026: "should look Fridge.com color branded
 * outline all that"): the badge was a solid black square. It is now a white
 * tile with a 2 px ring in the Fridge.com blue-to-orange gradient (the one the
 * hero's eyebrow dot uses) and the letter in Fridge.com blue, #0469f0 on white
 * at 4.95:1 (WCAG AA). The ring is a border-box gradient behind a white
 * padding-box fill, so the tile keeps its 36 px size and its place in the row.
 *
 * Logos view-mode toggle was removed in favour of always rendering the
 * list view — the bigger card with name + tier + price range is the
 * canonical browsing surface; the logo grid was a redundant alternative.
 */
/** White fill inside, the blue-to-orange brand gradient in the 2 px border. */
const BADGE_RING: React.CSSProperties = {
  background: "linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #0469f0, #ff9951) border-box",
}

export function BrandSection({ letter, brands, sectionRef }: BrandSectionProps) {
  if (brands.length === 0) return null
  const sectionId = `brands-${letter === "#" ? "num" : letter.toLowerCase()}`

  return (
    <section
      id={sectionId}
      data-letter={letter}
      ref={sectionRef as React.Ref<HTMLElement>}
      className="scroll-mt-[150px]"
    >
      <div className="my-10 flex items-center gap-3.5">
        <div
          className="grid h-9 w-9 place-items-center rounded-[10px] border-2 border-transparent text-[16px] font-black tracking-[-0.02em] text-[#0469f0]"
          style={BADGE_RING}
        >
          {letter}
        </div>
        <h2 className="text-[13px] font-bold uppercase tracking-[0.1em] text-gray-500">
          {brands.length} {brands.length === 1 ? "brand" : "brands"}
        </h2>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {brands.map(b => (
          <BrandCardList key={b.slug} brand={b} />
        ))}
      </div>
    </section>
  )
}
